import {
  combineLatest as observableCombineLatest,
  of as observableOf,
  forkJoin as observableForkJoin,
  Observable,
  BehaviorSubject,
  Subject,
} from 'rxjs';

import {
  mergeMap,
  catchError,
  shareReplay,
  distinctUntilChanged,
  map,
  first,
  takeWhile,
} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {Location} from '@angular/common';
import {ResourceService} from './resource.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Resource, ResourceWithPermissionHints} from './resource';
import {getResourceLabel} from './resource-label';
import {SearchScopeConfiguration} from '../cache/uiconfiguration/searchscopeconfiguration';
import {NavigationBarConfiguration} from '../cache/uiconfiguration/navigationbarconfiguration';
import {LayoutService} from '../layout/layout.service';
import {CacheService} from '../cache/cache.service';
import {MyHistory, MyHistoryItem} from './history';
import {ActionBarConfiguration} from '../cache/uiconfiguration/actionbarconfiguration';
import {DialogsService} from '../shared/dialog/dialogs.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SmdDataTable} from '../shared/component/smd-datatable/datatable.component';
import {ResourceDetailComponent} from './detail/detail.component';
import {FilterResult} from './filterresult';
import {LanguageService} from '../language/language.service';
import {ResourceWizardComponent, WizardType} from './detail/wizard.component';
import {AddViewEvent} from './events/addviewevent';
import {ListRoutingConfiguration} from '../cache/schema/listroutingconfiguration';
import {DialogConfiguration} from '../cache/uiconfiguration/dialogconfiguration';
import {Title} from '@angular/platform-browser';
import {EnvironmentConfigService} from '../core/services/environment-config.service';
import {CandeactivateDialogComponent} from './detail/controls/canDeactivateGuard/candeactivate-dialog.component';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {SmdTimeline} from '../shared/component/smd-timeline/timeline.component';

@Component({
  templateUrl: './resource.component.html',
  styleUrls: ['./resource.component.scss', './resource-datatable.scss', './history-nav-bar.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResourceComponent implements OnInit, OnDestroy {
  listActive: boolean = false;
  listTitle: string;
  listActionBarConfigs: ActionBarConfiguration[] = [];

  @ViewChild('list', {static: true}) private listDataTable: SmdDataTable;
  @ViewChild('timeline', {static: true}) private timeline: SmdTimeline;
  @ViewChild('detail1') private detail1Component: ResourceDetailComponent;
  @ViewChild('detail2') private detail2Component: ResourceDetailComponent;

  history: MyHistory;

  static leftClass: string = 'box-left';
  static rightClass: string = 'box-right';
  static fullClass: string = 'box-full';

  /** URL site type of every wizard type (the inverse of the addView switch) */
  private static readonly WIZARD_SITETYPES: {[type in WizardType]?: string} = {
    [WizardType.Create]: 'create',
    [WizardType.Single]: 'wizard',
    [WizardType.Multiple]: 'batch',
    [WizardType.BatchList]: 'batchlist',
    [WizardType.Approval]: 'approval',
    [WizardType.ApprovalMultiple]: 'approvalbatch',
  };

  resources: any[] = null;
  listClass: string = ResourceComponent.fullClass;

  detail1Resource: BehaviorSubject<ResourceWithPermissionHints> = null;
  detail1Class: string = ResourceComponent.fullClass;
  detail1startEdit: boolean = false;

  detail2Resource: BehaviorSubject<ResourceWithPermissionHints> = null;
  detail2Class: string = ResourceComponent.rightClass;
  detail2startEdit: boolean = false;

  timelineResourceID: BehaviorSubject<string> = null;
  timelineActive: boolean = false;
  timelineTitle: string = 'Timeline';
  timelineActionBarConfigs: ActionBarConfiguration[] = [];
  timelineClass: string = ResourceComponent.fullClass;

  wizardClass: string = ResourceComponent.rightClass;
  wizardActive: boolean = false;
  @ViewChild('wizard', {static: true}) private wizardComponent: ResourceWizardComponent;

  // Getter for template access to ObjectID attribute
  get objectIdAttr(): string {
    return this.envConfig.getAttributeName('ObjectID');
  }

  constructor(
    private resourceService: ResourceService,
    private layoutService: LayoutService,
    private cacheService: CacheService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private router: Router,
    private location: Location,
    private dialogsService: DialogsService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone,
    private titleService: Title,
    private dialog: MatDialog,
    private envConfig: EnvironmentConfigService,
  ) {}

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.route.params.pipe(takeWhile(() => this.alive)).subscribe(params => {
        let sitetype: string = params['sitetype'];
        let id: string = params['id'];
        let id2: string = params['id2'];

        if (sitetype === 'external') {
          this.router.navigate(['/404']); // for security reasons
          this.history = new MyHistory(this.titleService, this.cacheService);
          return;
        }

        this.ngZone.run(() => {
          this.history = new MyHistory(this.titleService, this.cacheService);
          this.addView(null, sitetype, id, id2, false, true);
        });
      });

      this.layoutService.resourceViewUpdate$.subscribe((historyItem: MyHistoryItem) => {
        this.ngZone.run(() => {
          this.history = new MyHistory(this.titleService, this.cacheService);
          this.addView(null, historyItem.sitetype, historyItem.id, historyItem.id2);
        });
      });
    });
  }

  private alive: boolean = true;

  ngOnDestroy(): void {
    this.listActive = false;
    this.timelineActive = false;
    this.detail1Resource = null;
    this.detail2Resource = null;
    this.timelineResourceID = null;
    this.wizardActive = false;
    this.alive = false;
  }

  canDeactivate(): Observable<boolean> | boolean {
    // Check if there is a !freshResource in one of the views -> if yes then display a warning dialog
    let dirtyResource1: boolean = false;
    let dirtyResource2: boolean = false;
    let dirtyResource3: boolean = false;
    if (this.detail1Component !== undefined) {
      dirtyResource1 = !this.detail1Component.freshResource;
    }
    if (this.detail2Component !== undefined) {
      dirtyResource2 = !this.detail2Component.freshResource;
    }
    if (this.wizardComponent !== undefined && this.wizardActive) {
      dirtyResource3 = !this.wizardComponent.freshResource;
    }
    return !(dirtyResource1 || dirtyResource2 || dirtyResource3);
  }

  breadcrumbClick(siteType: string, id: string, id2: string, index: number) {
    this.history.removeAfterIndex(index);
    this.addView(null, siteType, id, id2);
  }

  addViewFromEvent(sourceView: string, addViewEvent: AddViewEvent) {
    this.addView(
      sourceView,
      addViewEvent.siteType,
      addViewEvent.urlid,
      addViewEvent.id,
      addViewEvent.editEnabled,
    );
  }

  addView(
    sourceView: string,
    siteType: string,
    id: string,
    id2: string | string[],
    editEnabled: boolean = false,
    canDeactivate: boolean = false,
  ) {
    if (
      !canDeactivate &&
      !this.canDeactivate() &&
      !(siteType === 'get' && sourceView !== 'detail2')
    ) {
      let dialogRef: MatDialogRef<CandeactivateDialogComponent> = this.dialog.open(
        CandeactivateDialogComponent,
      );
      dialogRef.afterClosed().subscribe((leave: boolean) => {
        if (!leave) {
          return;
        } else {
          this.addView(sourceView, siteType, id, id2, editEnabled, true);
        }
      });
    } else {
      if (siteType === 'external') {
        window.location.href = id;
        return;
      }

      // other internal non-resource pages
      if (siteType === 'home') {
        this.router.navigate(['/home', id]);
        return;
      } else if (siteType === 'administration') {
        this.router.navigate(['/administration']);
        return;
      }

      if (siteType === 'objectlist') {
        siteType = 'olist';
      }

      let sourceViewClass: string = null;
      if (sourceView !== null) {
        sourceViewClass = this.getClassOfView(sourceView);
      }

      this.hideAllButSource(sourceView);

      if (sourceViewClass === null) {
        // open full width
        switch (siteType) {
          case 'list':
            this.listClass = ResourceComponent.fullClass;
            this.loadList(id);
            break;
          case 'get':
            this.loadGet(id, 1, ResourceComponent.fullClass, false, editEnabled);
            break;
          case 'timeline':
            this.timelineClass = ResourceComponent.fullClass;
            this.loadTimeline(id, ResourceComponent.fullClass, false);
            break;
          case 'olist':
            this.listClass = ResourceComponent.fullClass;
            this.loadObjectList(id);
            break;
          case 'create':
            this.wizardClass = ResourceComponent.fullClass;
            this.loadWizard(WizardType.Create, false, id);
            break;
          case 'wizard':
            this.wizardClass = ResourceComponent.fullClass;
            this.loadWizard(WizardType.Single, false, id, id2);
            break;
          case 'batch':
            this.wizardClass = ResourceComponent.fullClass;
            this.loadWizard(WizardType.Multiple, false, id, id2);
            break;
          case 'batchlist':
            this.wizardClass = ResourceComponent.fullClass;
            this.loadWizard(WizardType.BatchList, false, id, id2);
            break;
          case 'approval':
            this.wizardClass = ResourceComponent.fullClass;
            this.loadWizard(WizardType.Approval, false, id, id2);
            break;
          case 'approvalbatch':
            this.wizardClass = ResourceComponent.fullClass;
            this.loadWizard(WizardType.ApprovalMultiple, false, id, id2);
            break;
        }
      } else if (sourceViewClass === ResourceComponent.fullClass) {
        // open right, minimize left
        this.minimizeAll();
        switch (siteType) {
          case 'list':
            //TODO this is just a quick fix. implement second list.
            this.listClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.loadList(id);
            break;
          case 'get':
            if (sourceView === 'list') {
              this.loadGet(id, 1, ResourceComponent.rightClass, false, editEnabled);
            } else {
              this.loadGet(id, 2, ResourceComponent.rightClass, false, editEnabled);
            }
            break;
          case 'timeline':
            this.timelineClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.loadTimeline(id, ResourceComponent.fullClass, false);
            break;
          case 'olist':
            //TODO this is just a quick fix. implement second list.
            this.listClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.loadObjectList(id);
            break;
          case 'create':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Create, false, id);
            break;
          case 'wizard':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Single, false, id, id2);
            break;
          case 'batch':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Multiple, false, id, id2);
            break;
          case 'batchlist':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.BatchList, false, id, id2);
            break;
          case 'approval':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Approval, false, id, id2);
            break;
          case 'approvalbatch':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.ApprovalMultiple, false, id, id2);
            break;
        }
      } else if (sourceViewClass === ResourceComponent.leftClass) {
        // replace right
        switch (siteType) {
          case 'list':
            //TODO implement second list.
            this.listClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.history.removeLast();
            this.loadList(id);
            break;
          case 'get':
            if (sourceView === 'list') {
              this.loadGet(id, 1, ResourceComponent.rightClass, true, editEnabled);
            } else if (sourceView === 'detail1' || sourceView === 'timeline') {
              this.loadGet(id, 2, ResourceComponent.rightClass, true, editEnabled);
            }
            break;
          case 'timeline':
            this.timelineClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.loadTimeline(id, ResourceComponent.fullClass, true);
            break;
          case 'olist':
            //TODO implement second list.
            this.listClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.history.removeLast();
            this.loadObjectList(id);
            break;
          case 'create':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Create, true, id);
            break;
          case 'wizard':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Single, true, id, id2);
            break;
          case 'batch':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Multiple, true, id, id2);
            break;
          case 'batchlist':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.BatchList, true, id, id2);
            break;
          case 'approval':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Approval, true, id, id2);
            break;
          case 'approvalbatch':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.ApprovalMultiple, true, id, id2);
            break;
        }
      } else if (sourceViewClass === ResourceComponent.rightClass) {
        // move current to left and open right
        this.minimizeAll();
        switch (siteType) {
          case 'list':
            //TODO implement second list.
            this.listClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.loadList(id);
            break;
          case 'get':
            if (sourceView === 'detail2') {
              this.detail2Resource.pipe(first()).subscribe(det2res => {
                if (this.detail1Resource == null) {
                  this.detail1Resource = new BehaviorSubject(det2res);
                }
                this.detail1Resource.next(det2res);
                this.changeDetector.markForCheck();
              });
            }
            this.loadGet(id, 2, ResourceComponent.rightClass, false, editEnabled);
            break;
          case 'timeline':
            this.timelineClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.loadTimeline(id, ResourceComponent.fullClass, false);
            break;
          case 'olist':
            //TODO implement second list.
            this.listClass = ResourceComponent.fullClass;
            this.hideAllButSource(siteType);
            this.loadObjectList(id);
            break;
          case 'create':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Create, false, id);
            break;
          case 'wizard':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Single, false, id, id2);
            break;
          case 'batch':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Multiple, false, id, id2);
            break;
          case 'batchlist':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.BatchList, false, id, id2);
            break;
          case 'approval':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.Approval, false, id, id2);
            break;
          case 'approvalbatch':
            this.wizardClass = ResourceComponent.rightClass;
            this.loadWizard(WizardType.ApprovalMultiple, false, id, id2);
            break;
        }
      }
      let path = '/' + siteType + '/' + id;
      if (id2 != null) {
        path += '/' + id2;
      }
      this.location.go(path);
      window.scrollTo(null, 0);
      this.changeDetector.markForCheck();
    }
  }

  private getClassOfView(sourceView: string): string {
    switch (sourceView) {
      case 'list':
        return this.listClass;
      case 'detail1':
        return this.detail1Class;
      case 'detail2':
        return this.detail2Class;
      case 'timeline':
        return this.timelineClass;
      case 'wizard':
        return this.wizardClass;
    }
    return null;
  }

  public close(view: string, refreshActive: boolean = false) {
    switch (view) {
      case 'list':
        this.listActive = false;
        break;
      case 'detail1':
        this.detail1Resource = null;
        break;
      case 'detail2':
        this.detail2Resource = null;
        break;
      case 'timeline':
        this.timelineActive = false;
        break;
      case 'wizard':
        this.wizardActive = false;
        break;
    }

    let classOfView: string = this.getClassOfView(view);

    if (classOfView === ResourceComponent.rightClass) {
      this.history.removeLast();
      let item: MyHistoryItem = this.history.activeItem;
      if (item != null) {
        let path = '/' + item.sitetype + '/' + item.id;
        if (item.id2 != null) {
          path += '/' + item.id2;
        }
        this.location.go(path);
      }
    } else if (classOfView === ResourceComponent.fullClass) {
      this.history.removeLast();
    }

    if (this.history.activeItem == null) {
      this.router.navigate(['/home']);
      return;
    }

    this.maximizeAll();

    let activeView = null;
    if (this.listTitle) {
      activeView = this.listDataTable;
    } else if (this.detail1Resource != null) {
      activeView = this.detail1Component;
    } else if (this.detail2Resource != null) {
      activeView = this.detail2Component;
    } else if (this.timelineActive) {
      activeView = this.timeline;
    } else if (this.wizardActive) {
      activeView = this.wizardComponent;
    }

    if (
      activeView != null &&
      'onResize' in activeView &&
      typeof activeView.onResize === 'function'
    ) {
      setTimeout(() => {
        activeView.onResize();
      }, 100);
      setTimeout(() => {
        activeView.onResize();
      }, 500);
    }

    if (refreshActive === true) {
      this.refreshActiveView();
    }
    this.changeDetector.markForCheck();
  }

  public refreshActiveView() {
    let activeItem: MyHistoryItem = this.history.activeItem;
    if (activeItem == null) {
      this.router.navigate(['/home']);
      return;
    }

    let activeSiteType: string = activeItem.sitetype;
    if (activeSiteType === 'list') {
      this.refreshListData();
    } else if (activeSiteType === 'get') {
      // TODO dont load total component new, just refresh values.
      if (this.detail1Resource === null && this.detail2Resource === null) {
        this.loadGet(activeItem.id, 1, ResourceComponent.fullClass, null);
      } else if (this.detail1Resource !== null) {
        this.detail1Resource.pipe(first()).subscribe(det1Res => {
          if (det1Res[this.envConfig.getAttributeName('ObjectID')].Values === activeItem.id) {
            this.loadGet(activeItem.id, 1, this.detail1Class, null);
          }
          this.changeDetector.markForCheck();
        });
      } else if (this.detail2Resource !== null) {
        this.detail2Resource.pipe(first()).subscribe(det2Res => {
          if (det2Res[this.envConfig.getAttributeName('ObjectID')].Values === activeItem.id) {
            this.loadGet(activeItem.id, 2, this.detail2Class, null);
          }
          this.changeDetector.markForCheck();
        });
      } else if (
        this.detail1Resource !== null &&
        (this.detail2Resource === null || this.detail1Class === ResourceComponent.rightClass)
      ) {
        this.detail1Resource.pipe(first()).subscribe(det1Res => {
          this.loadGet(
            det1Res[this.envConfig.getAttributeName('ObjectID')].Values,
            1,
            this.detail1Class,
            null,
          );
          this.changeDetector.markForCheck();
        });
      } else if (
        this.detail2Resource !== null &&
        (this.detail1Resource === null || this.detail2Class === ResourceComponent.rightClass)
      ) {
        this.detail2Resource.pipe(first()).subscribe(det2Res => {
          this.loadGet(
            det2Res[this.envConfig.getAttributeName('ObjectID')].Values,
            2,
            this.detail2Class,
            null,
          );
          this.changeDetector.markForCheck();
        });
      }
    } else if (activeSiteType === 'timeline') {
      this.timeline.refreshData();
      if (this.timelineResourceID != null) {
        this.timelineResourceID.pipe(first()).subscribe((id: string) => {
          if (id != null) {
            this.loadTimeline(id, this.timelineClass, null);
          }
        });
      }
    } else if (activeSiteType === 'olist') {
      this.loadObjectList(activeItem.id, false);
    } else if (activeSiteType === 'wizard') {
      // TODO REFRESH WIZARD
    }
    this.changeDetector.markForCheck();
  }

  public refreshListData() {
    if (this.listDataTable != null) {
      this.listDataTable.refreshData();
    }
  }

  private loadList(urlId: string) {
    let searchScopeConfigurations: SearchScopeConfiguration[] = [];

    this.cacheService
      .getListRoutingConfigurationByUrlID(urlId, 'list')
      .pipe(first())
      .subscribe((listConf: ListRoutingConfiguration) => {
        if (listConf == null) return;
        if (listConf.defaultSearchScope) {
          this.cacheService
            .getSearchScopeConfigurationById(listConf.defaultSearchScope)
            .pipe(first())
            .subscribe(
              (defaultSsc: SearchScopeConfiguration) => {
                searchScopeConfigurations = [defaultSsc];

                this.cacheService
                  .getSearchScopeConfigurationForObjectType(urlId)
                  .pipe(first())
                  .subscribe((sscs: SearchScopeConfiguration[]) => {
                    sscs = sscs.filter(value => {
                      return value.id !== defaultSsc.id;
                    });
                    searchScopeConfigurations = searchScopeConfigurations.concat(sscs);

                    this.listDataTable.reloadList(searchScopeConfigurations, defaultSsc);
                  });
              },
              () => {
                this.cacheService
                  .getSearchScopeConfigurationForObjectType(urlId)
                  .pipe(first())
                  .subscribe((sscs: SearchScopeConfiguration[]) => {
                    sscs = sscs.sort((a, b) => a.order - b.order);
                    searchScopeConfigurations = searchScopeConfigurations.concat(sscs);

                    this.listDataTable.reloadList(searchScopeConfigurations);
                  });
              },
            );
        } else {
          this.cacheService
            .getSearchScopeConfigurationForObjectType(urlId)
            .pipe(first())
            .subscribe((sscs: SearchScopeConfiguration[]) => {
              sscs = sscs.sort((a, b) => a.order - b.order);
              searchScopeConfigurations = searchScopeConfigurations.concat(sscs);

              this.listDataTable.reloadList(searchScopeConfigurations);
            });
        }
        this.listTitle = listConf.displayName;
        this.listActionBarConfigs = [];
        if (
          !(listConf.actionBarResources === null || listConf.actionBarResources === undefined) &&
          listConf.actionBarResources.length > 0
        ) {
          let abResObs: Observable<ActionBarConfiguration>[] = [];
          listConf.actionBarResources.forEach((value: string) => {
            if (
              !(
                this.cacheService.getActionBarConfigurationById(value) === null ||
                this.cacheService.getActionBarConfigurationById(value) === undefined
              )
            ) {
              abResObs.push(this.cacheService.getActionBarConfigurationById(value).pipe(first()));
            }
          });

          if (abResObs.length > 0) {
            observableForkJoin(abResObs)
              .pipe(first())
              .subscribe((abcs: ActionBarConfiguration[]) => {
                this.listActionBarConfigs = abcs;
                this.changeDetector.markForCheck();
              });
          }
        }

        this.listActive = true;
        this.history.add(
          new MyHistoryItem(
            listConf.siteType,
            urlId,
            null,
            observableOf(listConf.displayName),
            this.cacheService
              .getDisplayAttribute(
                this.envConfig.getAttributeName('DisplayName'),
                this.envConfig.getObjectTypeName('NavigationBarConfiguration'),
              )
              .pipe(map(x => x.Caption)),
            this.cacheService.getLanguageResource().pipe(first()),
          ),
        );
        this.changeDetector.markForCheck();
      });
  }

  private loadObjectList(objectType: string, addToHistory: boolean = true) {
    const obs = this.resourceService
      .searchResources(
        '/' +
          this.envConfig.getObjectTypeName('ObjectTypeDescription') +
          '[' +
          this.envConfig.getAttributeName('Name') +
          '="' +
          objectType +
          '"]',
        this.envConfig.getAttributeName('DisplayName'),
        1,
      )
      .pipe(distinctUntilChanged(), shareReplay(1));
    if (addToHistory === true) {
      const hi = new MyHistoryItem(
        'olist',
        objectType,
        null,
        obs.pipe(
          map(r => {
            if (
              r.Results[0] == null ||
              r.Results[0][this.envConfig.getAttributeName('DisplayName')] == null ||
              r.Results[0][this.envConfig.getAttributeName('DisplayName')].length === 0
            ) {
              return ResourceService.convertCamelCaseToNormal(objectType);
            } else {
              return r.Results[0][this.envConfig.getAttributeName('DisplayName')];
            }
          }),
        ),
        this.cacheService
          .getDisplayAttribute(this.envConfig.getAttributeName('DisplayName'), objectType)
          .pipe(map(x => x.Caption)),
        this.cacheService.getLanguageResource().pipe(first()),
      );
      this.history.add(hi);
    }
    obs.pipe(first()).subscribe((res: FilterResult) => {
      if (res.Results != null && Array.isArray(res.Results) && res.Results.length > 0) {
        let displayName: string = res.Results[0][this.envConfig.getAttributeName('DisplayName')];
        if (displayName.length === 0) {
          displayName = ResourceService.convertCamelCaseToNormal(objectType);
        }

        this.cacheService
          .getSearchScopeConfigurationForObjectType(objectType)
          .pipe(first())
          .subscribe((sscs: SearchScopeConfiguration[]) => {
            sscs = sscs.sort((a, b) => a.order - b.order);
            if (sscs.length === 0) {
              if (displayName == null || displayName.length == 0) {
                displayName = ResourceService.convertCamelCaseToNormal(objectType);
              }
              sscs.push(<SearchScopeConfiguration>{
                id: null,
                displayname: displayName,
                description: displayName,
                usagekeyword: [],
                order: 0,
                attributesearched: [
                  this.envConfig.getAttributeName('DisplayName'),
                  this.envConfig.getAttributeName('Description'),
                ],
                xpath: '/' + objectType,
                resultobjecttype: objectType,
                attributedisplayed: [
                  this.envConfig.getAttributeName('DisplayName'),
                  this.envConfig.getAttributeName('Description'),
                  this.envConfig.getAttributeName('CreatedTime'),
                ],
              });
            }
            this.listDataTable.reloadList(sscs);
          });

        this.listTitle = displayName;

        this.cacheService
          .getActionBarConfigurationsForObjectType(objectType)
          .pipe(first())
          .subscribe((actionbars: ActionBarConfiguration[]) => {
            this.listActionBarConfigs = actionbars;
            this.listActionBarConfigs.push(
              ActionBarConfiguration.DefaultFilter,
              ActionBarConfiguration.DefaultEdit,
              ActionBarConfiguration.DefaultDelete,
              ActionBarConfiguration.DefaultCreate,
            );
          });

        this.listActive = true;
        this.listDataTable.onResize();
        this.changeDetector.markForCheck();
      } else {
        this.cacheService
          .getLanguageResource()
          .pipe(first())
          .subscribe(lr => {
            const title: string = LanguageService.translate(
              'ResourceLoadObjectlistErrorTitle',
              'Error loading Object List',
              lr,
            );
            const message: string = LanguageService.translate(
              'ResourceLoadObjectlistErrorMessage',
              'The Resource Type {0} can not be found in the system.',
              lr,
              [objectType],
            );

            this.dialogsService.error(title, message, null).subscribe(() => {
              this.close('list', true);
            });
          });
        this.changeDetector.markForCheck();
      }
    });
  }

  handleError(error: any, caught?: Observable<any>) {
    console.error(error);
    return observableOf(null);
  }

  actionBarClick(conf: ActionBarConfiguration, selectedObjectIds: string | string[]) {
    if (conf.sitetype === 'objectlist') conf.sitetype = 'olist';
    if (['get', 'timeline'].indexOf(conf.sitetype) > -1) {
      this.addView('list', conf.sitetype, conf.linkedobject, null);
    } else if (['list', 'olist', 'external'].indexOf(conf.sitetype) > -1) {
      this.addView('list', conf.sitetype, conf.urlid, null);
    } else if (conf.sitetype === 'create') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this.addView('list', 'create', dgConf.urlid, null);
        });
    } else if (conf.sitetype === 'wizard') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this.addView('list', 'wizard', dgConf.urlid, selectedObjectIds);
        });
    } else if (conf.sitetype === 'batch') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this.addView('list', 'batch', dgConf.urlid, selectedObjectIds);
        });
    } else if (conf.sitetype === 'batchlist') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this.addView('list', 'batchlist', dgConf.urlid, selectedObjectIds);
        });
    } else if (conf.sitetype === 'approval') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this.addView('list', 'approval', dgConf.urlid, selectedObjectIds);
        });
    } else if (conf.sitetype === 'approvalbatch') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this.addView('list', 'approvalbatch', dgConf.urlid, selectedObjectIds);
        });
    } else {
      let command: any[] = [''];
      if (conf.sitetype != null) command.push(conf.sitetype);
      if (conf.urlid != null) command.push(conf.urlid);
      if (conf.linkedobject != null) command.push(conf.linkedobject);
      this.router.navigate(command);
    }

    this.changeDetector.markForCheck();
  }

  listDelete(resources: Resource[]) {
    if (resources == null || resources.length === 0) return;
    this.cacheService
      .getLanguageResource()
      .pipe(first())
      .subscribe(lr => {
        let title: string;
        let message: string;
        if (resources.length === 1) {
          let object = '';
          if (
            resources[0][this.envConfig.getAttributeName('DisplayName')] != null &&
            resources[0][this.envConfig.getAttributeName('DisplayName')].length > 0
          ) {
            object += resources[0][this.envConfig.getAttributeName('DisplayName')];
          } else {
            object += resources[0][this.envConfig.getAttributeName('ObjectID')];
          }
          message = LanguageService.translate(
            'ResourceDetailDeleteResourceMessage',
            "Do you really want to delete '{0}'?",
            lr,
            [object],
          );
          title = LanguageService.translate('ResourceDetailDeleteResource', 'Delete resource', lr);
        } else {
          let displayNames: string[] = [];
          resources.forEach((res: Resource) => {
            if (
              res[this.envConfig.getAttributeName('DisplayName')] == null ||
              res[this.envConfig.getAttributeName('DisplayName')].length === 0
            ) {
              displayNames.push(
                '<li>' + res[this.envConfig.getAttributeName('ObjectID')] + '</li>',
              );
            } else {
              displayNames.push(
                '<li>' + res[this.envConfig.getAttributeName('DisplayName')] + '</li>',
              );
            }
          });
          let listHtml: string = '<ul>' + displayNames.join('') + '</ul>';
          message = LanguageService.translate(
            'ResourceDetailDeleteResourcesMessage',
            'Do you really want to delete those {0} resources? {1}',
            lr,
            ['' + resources.length, listHtml],
          );
          title = LanguageService.translate(
            'ResourceDetailDeleteResources',
            'Delete resources',
            lr,
          );
        }

        this.dialogsService
          .confirm(
            title,
            message,
            LanguageService.translate('Yes', 'Yes', lr),
            LanguageService.translate('No', 'No', lr),
          )
          .subscribe((choice: any) => {
            if (choice === true) {
              let toBeDeleted: number = resources.length;
              resources.forEach((res: Resource) => {
                let desc = res[this.envConfig.getAttributeName('ObjectID')];
                if (
                  res[this.envConfig.getAttributeName('DisplayName')] != null &&
                  res[this.envConfig.getAttributeName('DisplayName')].length > 0
                ) {
                  desc = res[this.envConfig.getAttributeName('DisplayName')];
                }
                this.resourceService
                  .deleteResource(res[this.envConfig.getAttributeName('ObjectID')])
                  .pipe(first())
                  .subscribe(
                    (ok: boolean) => {
                      const message: string = LanguageService.translate(
                        'ResourceDetailDeleteSuccess',
                        'Resource {0} deleted successfully.',
                        lr,
                        [desc],
                      );
                      this.snackBar.open(message, undefined, {duration: 1000});

                      toBeDeleted--;
                      if (toBeDeleted == 0) {
                        this.listDataTable.checked = false;
                        this.listDataTable.refreshData();
                      }
                      this.changeDetector.markForCheck();
                    },
                    (error: any) => {
                      const title: string = LanguageService.translate(
                        'ResourceDeleteErrorTitle',
                        'Deleting resource failed.',
                        lr,
                      );
                      const message: string = LanguageService.translate(
                        'ResourceDeleteErrorMessage',
                        'An error occurred by deleting {0}.',
                        lr,
                        [desc],
                      );
                      this.dialogsService.webserviceError(error, title, message, lr);

                      toBeDeleted--;
                      if (toBeDeleted == 0) {
                        this.listDataTable.checked = false;
                        this.listDataTable.refreshData();
                      }
                      this.changeDetector.markForCheck();
                    },
                  );
              });
            }
          });
      });
  }

  private loadGet(
    id: string,
    destinationView: number,
    detailClass: string,
    replaceHistory: boolean,
    editEnabled: boolean = false,
  ) {
    this.layoutService.updatePreloaderState('active');
    const bs = new BehaviorSubject(null);

    const obs = this.resourceService
      .getResourceWithPermissionHints(id)
      .pipe(distinctUntilChanged(), shareReplay(1));
    let histItem: MyHistoryItem = new MyHistoryItem(
      'get',
      id,
      null,
      bs.pipe(
        map(r =>
          r != null && r[this.envConfig.getAttributeName('DisplayName')] != null
            ? r[this.envConfig.getAttributeName('DisplayName')].Values
            : null,
        ),
      ),
      bs.pipe(
        mergeMap(r =>
          r != null && r[this.envConfig.getAttributeName('ObjectType')] != null
            ? this.cacheService
                .getDisplayAttribute(
                  this.envConfig.getAttributeName('DisplayName'),
                  r[this.envConfig.getAttributeName('ObjectType')].Values,
                )
                .pipe(map(x => x.Caption))
            : observableOf(null),
        ),
      ),
      this.cacheService.getLanguageResource().pipe(first()),
    );
    if (replaceHistory === true) {
      this.history.replaceLast(histItem);
    } else if (replaceHistory === false) {
      this.history.add(histItem);
    }

    obs.pipe(first()).subscribe(
      (resource: ResourceWithPermissionHints) => {
        if (destinationView !== 2) {
          this.detail1startEdit = editEnabled === true;
          this.detail1Resource = bs;
          this.detail1Resource.next(resource);
          this.detail1Class = detailClass;
        } else {
          this.detail2startEdit = editEnabled === true;
          this.detail2Resource = bs;
          this.detail2Resource.next(resource);
          this.detail2Class = detailClass;
        }
        this.changeDetector.markForCheck();
        this.layoutService.updatePreloaderState('hide');

        this.changeDetector.markForCheck();
        this.listDataTable.onResize();
        this.timeline.onResize();
      },
      (error: any) => {
        if (destinationView == 2) {
          this.detail2Resource = null;
          this.maximizeAll();
        } else {
          this.detail1Resource = null;
          this.maximizeAll();
        }

        this.cacheService
          .getLanguageResource()
          .pipe(first())
          .subscribe(lr => {
            const title: string = LanguageService.translate(
              'ResourceLoadDetailErrorTitle',
              'Resource Not Found',
              lr,
            );
            const message: string = LanguageService.translate(
              'ResourceLoadDetailErrorMessage',
              'Maybe the resource got deleted or you are missing permissions to view this resource.',
              lr,
            );
            const additionalMessage: string = LanguageService.translate(
              'ResourceLoadDetailErrorAdditionalMessage',
              'Error loading the resource {0}.',
              lr,
              [id],
            );

            this.dialogsService
              .advancedWebserviceError(error, title, message, additionalMessage, lr)
              .subscribe(() => {
                this.layoutService.updatePreloaderState('hide');

                if (replaceHistory === true) {
                  this.history.removeLast();
                }
                let item: MyHistoryItem = this.history.activeItem;
                if (item == null) {
                  this.router.navigate(['/home']);
                } else {
                  let path = '/' + item.sitetype + '/' + item.id;
                  if (item.id2 != null) {
                    path += '/' + item.id2;
                  }
                  this.location.go(path);
                }
                this.changeDetector.markForCheck();
              });
          });
        this.changeDetector.markForCheck();
        this.listDataTable.onResize();
        this.timeline.onResize();
      },
    );
  }

  private loadTimeline(id: string, detailClass: string, replaceHistory: boolean) {
    this.timelineActionBarConfigs = [];
    // if id is a guid than the timeline of this object gets displayed, otherwise it's a list timeline and searching for searchscopes with id as usagekeywords
    // expects searchscopes
    if (ResourceService.guidRegex.test(id)) {
      // single object timeline
      this.layoutService.updatePreloaderState('active');
      //changed to behaviour subject for refresh on title click
      if (this.timelineResourceID == null) {
        this.timelineResourceID = new BehaviorSubject(id);
      } else {
        this.timelineResourceID.next(id);
      }

      let searchScopeConfigurations: SearchScopeConfiguration[] = [];

      const obs = this.resourceService
        .getBasicResource(id)
        .pipe(distinctUntilChanged(), shareReplay(1));
      obs.pipe(first()).subscribe(res => this.layoutService.updatePreloaderState('hide'));
      obs.subscribe((resource: Resource) => {
        searchScopeConfigurations.push(<SearchScopeConfiguration>{
          id: 'custom',
          displayname: resource?.[this.envConfig.getAttributeName('DisplayName')],
          description: 'string',
          usagekeyword: [],
          order: 0,
          attributesearched: [
            this.envConfig.getAttributeName('DisplayName'),
            this.envConfig.getAttributeName('RequestParameter'),
          ].filter((a): a is string => a != null),
          xpath:
            '/' +
            this.envConfig.getObjectTypeName('Request') +
            '[' +
            this.envConfig.getAttributeName('Target') +
            "='" +
            id +
            "']",
          resultobjecttype: this.envConfig.getObjectTypeName('Request'),
          attributedisplayed: [
            this.envConfig.getAttributeName('DisplayName'),
            this.envConfig.getAttributeName('CreatedTime'),
            this.envConfig.getAttributeName('Creator'),
            this.envConfig.getAttributeName('Operation'),
            this.envConfig.getAttributeName('RequestStatus'),
            this.envConfig.getAttributeName('Target'),
            this.envConfig.getAttributeName('ParentRequest'),
            this.envConfig.getAttributeName('ManagementPolicy'),
            this.envConfig.getAttributeName('RequestStatusDetail'),
            this.envConfig.getAttributeName('ActionWorkflowInstance'),
            this.envConfig.getAttributeName('AuthorizationWorkflowInstance'),
            this.envConfig.getAttributeName('AuthenticationWorkflowInstance'),
            this.envConfig.getAttributeName('RequestParameter'),
          ]
            .filter((a): a is string => a != null)
            .join(';'),
          sortingattribute: this.envConfig.getAttributeName('CreatedTime'),
          sortdirection: 'desc',
        });
        this.timeline.reloadList(searchScopeConfigurations, true);
        //this.timelineClass = detailClass; // still needed?
      });

      let histItem: MyHistoryItem = new MyHistoryItem(
        'timeline',
        id,
        null,
        obs.pipe(
          mergeMap((resource: Resource) => {
            if (resource != null) {
              this.timelineTitle = resource[this.envConfig.getAttributeName('DisplayName')];
              return observableOf(resource[this.envConfig.getAttributeName('DisplayName')]);
            } else {
              this.cacheService
                .getLanguageResource()
                .pipe(
                  first(),
                  map(
                    lr =>
                      '⟨ ' +
                      LanguageService.translate(
                        'TimelineInvalidObjectCaption',
                        'Deleted Resource',
                        lr,
                      ) +
                      ' ⟩',
                  ),
                )
                .subscribe((title: string) => {
                  this.timelineTitle = title;
                });

              return this.cacheService.getLanguageResource().pipe(
                first(),
                map(
                  lr =>
                    '⟨ ' +
                    LanguageService.translate(
                      'TimelineInvalidObjectCaption',
                      'Deleted Resource',
                      lr,
                    ) +
                    ' ⟩',
                ),
              );
            }
          }),
        ),
        obs.pipe(
          mergeMap(r =>
            this.cacheService
              .getDisplayAttribute(
                this.envConfig.getAttributeName('DisplayName'),
                r
                  ? r[this.envConfig.getAttributeName('ObjectType')]
                  : this.envConfig.getObjectTypeName('Resource'),
              )
              .pipe(map(x => x.Caption)),
          ),
        ),
        this.cacheService.getLanguageResource().pipe(first()),
      );

      let abResObs: Observable<ActionBarConfiguration>[] = [];
      abResObs.push(
        this.cacheService.getActionBarConfigurationByDisplayName('defaultFilter').pipe(first()),
      );
      if (abResObs.length > 0) {
        observableForkJoin(abResObs)
          .pipe(first())
          .subscribe((abcs: ActionBarConfiguration[]) => {
            this.timelineActionBarConfigs = abcs;
            this.changeDetector.markForCheck();
          });
      }

      this.timelineActive = true;
      if (replaceHistory === true) {
        this.history.replaceLast(histItem);
      } else if (replaceHistory === false) {
        this.history.add(histItem);
      }

      histItem.name.pipe(takeWhile(() => this.alive)).subscribe(t => this.timelineTitle);
      this.changeDetector.markForCheck();

      /* history behaviour
            const obs = this.resourceService.getBasicResource(id).pipe(distinctUntilChanged(),shareReplay(1),);
            obs.pipe(first()).subscribe(res => this.layoutService.updatePreloaderState('hide'));
            let histItem: MyHistoryItem = new MyHistoryItem('timeline', id, null,
                obs.pipe(mergeMap((resource: Resource) => {
                    if (resource != null) {
                        return observableOf(resource[this.envConfig.getAttributeName('DisplayName')]);
                    } else {
                        return this.cacheService.getLanguageResource().pipe(first(),
                            map(lr => "⟨ " + LanguageService.translate("TimelineInvalidObjectCaption", "Deleted Resource", lr) + " ⟩"),)
                    }
                })),
                obs.pipe(mergeMap(r => this.cacheService.getDisplayAttribute(this.envConfig.getAttributeName('DisplayName'), r ? r[this.envConfig.getAttributeName('ObjectType')] : this.envConfig.getObjectTypeName('Resource')).pipe(map(x => x.Caption)))),
                this.cacheService.getLanguageResource().pipe(first())
            );

            if (replaceHistory === true) {
                this.history.replaceLast(histItem);
            } else if (replaceHistory === false) {
                this.history.add(histItem);
            }

            histItem.name.pipe(takeWhile(() => this.alive)).subscribe(t => this.timelineTitle);
            this.changeDetector.markForCheck();
            */
    } else {
      // list (searchscope) timeline
      let searchScopeConfigurations: SearchScopeConfiguration[] = [];

      this.cacheService
        .getListRoutingConfigurationByUrlID(id, 'timeline')
        .pipe(first())
        .subscribe((listConf: ListRoutingConfiguration) => {
          if (listConf == null) return;
          if (listConf.defaultSearchScope) {
            this.cacheService
              .getSearchScopeConfigurationById(listConf.defaultSearchScope)
              .pipe(first())
              .subscribe(
                (defaultSsc: SearchScopeConfiguration) => {
                  searchScopeConfigurations = [defaultSsc];

                  this.cacheService
                    .getSearchScopeConfigurationForObjectType(id)
                    .pipe(first())
                    .subscribe((sscs: SearchScopeConfiguration[]) => {
                      sscs = sscs.filter(value => {
                        return value.id !== defaultSsc.id;
                      });
                      searchScopeConfigurations = searchScopeConfigurations.concat(sscs);

                      this.timeline.reloadList(searchScopeConfigurations, false, defaultSsc);
                    });
                },
                () => {
                  this.cacheService
                    .getSearchScopeConfigurationForObjectType(id)
                    .pipe(first())
                    .subscribe((sscs: SearchScopeConfiguration[]) => {
                      sscs = sscs.sort((a, b) => a.order - b.order);
                      searchScopeConfigurations = searchScopeConfigurations.concat(sscs);

                      this.timeline.reloadList(searchScopeConfigurations);
                    });
                },
              );
          } else {
            this.cacheService
              .getSearchScopeConfigurationForObjectType(id)
              .pipe(first())
              .subscribe((sscs: SearchScopeConfiguration[]) => {
                sscs = sscs.sort((a, b) => a.order - b.order);
                searchScopeConfigurations = searchScopeConfigurations.concat(sscs);

                this.timeline.reloadList(searchScopeConfigurations);
              });
          }
          this.timelineTitle = listConf.displayName;

          if (
            !(listConf.actionBarResources === null || listConf.actionBarResources === undefined) &&
            listConf.actionBarResources.length > 0
          ) {
            let abResObs: Observable<ActionBarConfiguration>[] = [];
            listConf.actionBarResources.forEach((value: string) => {
              if (
                !(
                  this.cacheService.getActionBarConfigurationById(value) === null ||
                  this.cacheService.getActionBarConfigurationById(value) === undefined
                )
              ) {
                abResObs.push(this.cacheService.getActionBarConfigurationById(value).pipe(first()));
              }
            });

            if (abResObs.length > 0) {
              observableForkJoin(abResObs)
                .pipe(first())
                .subscribe((abcs: ActionBarConfiguration[]) => {
                  this.timelineActionBarConfigs = abcs;
                  this.changeDetector.markForCheck();
                });
            }
          }

          this.timelineActive = true;
          this.history.add(
            new MyHistoryItem(
              listConf.siteType,
              id,
              null,
              observableOf(listConf.displayName),
              this.cacheService
                .getDisplayAttribute(
                  this.envConfig.getAttributeName('DisplayName'),
                  this.envConfig.getObjectTypeName('NavigationBarConfiguration'),
                )
                .pipe(map(x => x.Caption)),
              this.cacheService.getLanguageResource().pipe(first()),
            ),
          );
          this.changeDetector.markForCheck();
        });
    }
  }

  private loadWizard(
    wizardType: WizardType,
    replaceHistory: boolean,
    urlId: string,
    objectIds?: string | string[],
  ) {
    const historyTitleSubject: Subject<string> = new BehaviorSubject<string>(null);
    const histItem = new MyHistoryItem(
      ResourceComponent.WIZARD_SITETYPES[wizardType] ?? 'unknown',
      urlId,
      Array.isArray(objectIds) ? objectIds.join(',') : objectIds,
      historyTitleSubject.asObservable(),
    );

    if (replaceHistory === true) {
      this.history.replaceLast(histItem);
    } else if (replaceHistory === false) {
      this.history.add(histItem);
    }

    this.wizardComponent.init(wizardType, urlId, historyTitleSubject, objectIds);
    this.wizardActive = true;
  }

  private minimizeAll() {
    this.listClass = ResourceComponent.leftClass;
    this.detail1Class = ResourceComponent.leftClass;
    this.detail2Class = ResourceComponent.leftClass;
    this.timelineClass = ResourceComponent.leftClass;
    this.wizardClass = ResourceComponent.leftClass;
  }

  private maximizeAll() {
    this.listClass = ResourceComponent.fullClass;
    this.detail1Class = ResourceComponent.fullClass;
    this.detail2Class = ResourceComponent.fullClass;
    this.timelineClass = ResourceComponent.fullClass;
    this.wizardClass = ResourceComponent.fullClass;
  }

  hideAllButSource(sourceView: string) {
    if (sourceView !== 'list') this.listActive = false;
    if (sourceView !== 'detail1') this.detail1Resource = null;
    if (sourceView !== 'detail2') this.detail2Resource = null;
    if (sourceView !== 'timeline') this.timelineActive = null;
    if (sourceView !== 'wizard') this.wizardActive = false;
  }

  getResourceLabel(resource: ResourceWithPermissionHints): Observable<string> {
    return getResourceLabel(resource, this.cacheService, this.envConfig);
  }

  handleErrorInView(viewId: string, error: any) {
    this.cacheService
      .getLanguageResource()
      .pipe(first())
      .subscribe(
        lr => {
          const title = LanguageService.translate('ResourceErrorTitle', 'Error', lr);
          let msg = LanguageService.translate(
            'ResourceErrorMessage',
            'An error occurred in a view. The view will be closed now.',
            lr,
          );
          this.dialogsService
            .webserviceError(error, title, msg, lr)
            .subscribe(() => this.close(viewId));
        },
        () => this.close(viewId),
      );
  }
}
